 // frontend/src/components/PaymentSuccess-REFACTORED.tsx
// ✅ KEY CHANGES: Uses useAuth() to ensure user is authenticated before redirect
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'  // ✅ USE THIS
import { Check, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'

export default function PaymentSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshUser, setUser } = useAuth()  // ✅ USE CONTEXT
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const email = searchParams.get('email')
  const plan = searchParams.get('plan')
  const orderId = searchParams.get('token')

  useEffect(() => {
    const confirmPayment = async () => {
      try {
        setLoading(true)
        setError(null)

        // ✅ CHECK IF COMMUNITY SUBSCRIPTION PAYMENT
const urlParams = new URLSearchParams(window.location.search)
const type = urlParams.get('type')
const communityId = urlParams.get('communityId')
const orderIdFromUrl = urlParams.get('orderId')

if (type === 'community' && communityId && orderIdFromUrl) {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
    const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'}/subscription/capture-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ orderId: orderIdFromUrl, communityId })
    })

    const data = await res.json()

    if (data.success) {
      setSuccess(true)
      toast.success('Subscription activated! Welcome to the community! 🎉')
      setTimeout(() => navigate(`/community/${communityId}`, { replace: true }), 2000)
      return
    } else {
      throw new Error(data.error || 'Failed to activate subscription')
    }
  } catch (err: any) {
    setError(err.message || 'Failed to activate subscription')
    setLoading(false)
    return
  }
}

// ... rest of your existing confirmPayment code continues here

        if (!email) {
          throw new Error('Email not found in payment confirmation')
        }

        console.log(`💳 Confirming payment for ${email}...`)

        // Call backend to confirm payment and update user plan
        const response = await fetch('https://emailai-backend-658e.onrender.com/api/plans/confirm-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            email,
            plan,
            orderId,
            token: orderId
          })
        })

        if (!response.ok) {
          throw new Error('Failed to confirm payment')
        }

        const data = await response.json()

        if (data.success && data.paymentStatus === 'completed') {
          setSuccess(true)
          toast.success('🎉 Payment successful! Welcome aboard!')

          console.log('✅ Payment confirmed successfully')

          // ✅ UPDATE USER IN CONTEXT with new plan
          if (data.selectedPlan) {
            const updatedUserData = {
              ...user,
              selectedPlan: data.selectedPlan,
              plan: data.selectedPlan,
              paymentDate: new Date().toISOString(),
              paypalTransactionId: orderId || undefined
            }
            setUser(updatedUserData as any)
          }

          // ✅ REFRESH USER DATA from backend to ensure sync
          setTimeout(async () => {
            await refreshUser()
            
            // ✅ REDIRECT TO DASHBOARD after refresh completes
            navigate('/dashboard', { replace: true })
          }, 1500)
        } else if (data.paymentStatus === 'pending') {
          throw new Error('Payment is still being processed. Please wait and refresh the page.')
        } else {
          throw new Error(data.message || 'Payment confirmation failed. Please contact support.')
        }
      } catch (err: any) {
        console.error('❌ Payment confirmation error:', err)
        const errorMsg = err.message || 'Failed to confirm payment. Please contact support.'
        setError(errorMsg)
        toast.error(errorMsg)
        setLoading(false)
      }
    }

    confirmPayment()
  }, [])

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="mb-6">
            <svg className="animate-spin h-16 w-16 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </div>
      </div>
    )
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mb-4">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/plan-selection')}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-white text-gray-700 py-2 px-4 rounded-lg font-semibold border-2 border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-600">
                Need help? Contact our support team at{' '}
                <a href="mailto:support@emailai.pro" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                  support@emailai.pro
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
            {/* Success Icon */}
            <div className="mb-6 flex items-center justify-center">
              <div className="relative">
                <Check className="h-20 w-20 text-green-500 animate-pulse" />
              </div>
            </div>

            {/* Success Message */}
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Payment Successful! 🎉
            </h2>
            <p className="text-gray-600 mb-8">
              Thank you for your purchase! Your account is now active and ready to use.
            </p>

            {/* Plan Details */}
            {plan && (
              <div className="bg-indigo-50 rounded-lg p-4 mb-6 border border-indigo-200">
                <p className="text-sm text-gray-600 mb-1">Your Plan</p>
                <p className="text-xl font-bold text-indigo-600 capitalize">
                  {plan} Plan ✨
                </p>
              </div>
            )}

            {/* Email Confirmation */}
            {email && (
              <div className="bg-gray-50 rounded-lg p-4 mb-8 border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Confirmation sent to:</p>
                <p className="text-gray-900 font-semibold break-all">{email}</p>
              </div>
            )}

            {/* Benefits */}
            <div className="mb-8 text-left">
              <p className="text-sm font-bold text-gray-900 mb-4">You now have access to:</p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">Full Dashboard Access</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">AI-Powered Email Generator</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">Email Campaign Management</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm">Priority Support</span>
                </li>
              </ul>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 px-4 rounded-lg font-bold hover:from-indigo-700 hover:to-blue-700 transition-all transform hover:scale-105 mb-4 text-lg"
            >
              Go to Dashboard →
            </button>

            {/* Auto Redirect Notice */}
            <p className="text-xs text-gray-500">
              Automatically redirecting in a few seconds...
            </p>
          </div>

          {/* Support Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Questions? Email us at{' '}
              <a href="mailto:support@emailai.pro" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                support@emailai.pro
              </a>
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}